import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface ShopItem {
  id: string;
  name: string;
  image: string;
  description: string;
  whyMatch: string;
  tags: string[];
  reviewSnippet: string;
  reviews: number;
}

interface ShopCardsProps {
  shops: ShopItem[];
}

export default function ShopCards({ shops }: ShopCardsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-cream-light px-5 py-10 md:px-8 md:py-14">
      <div
        className={`mx-auto max-w-[480px] transition-all duration-700 md:max-w-[700px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="mb-6 flex items-center gap-2">
          <div className="h-px w-6 bg-softgold/40" />
          <span className="text-[11px] font-medium tracking-wider text-softgold">
            추천 업체
          </span>
        </div>

        <p className="mb-7 text-[13px] leading-relaxed text-navy/60 md:text-[14px]">
          이 기준에 맞는 인증 예복점을 선별했어요.
          <br />
          단순히 유명한 업체가 아니라, 당신의 성향과 가장 잘 맞는 업체입니다.
        </p>

        {/* mobile: horizontal scroll */}
        <div className="flex gap-3 overflow-x-auto pb-2 md:hidden snap-x snap-mandatory scrollbar-hide">
          {shops.map((shop, i) => (
            <Link key={i} to='/yangbok/detail/test'>
              <div
                key={shop.id}
                className="w-[280px] shrink-0 snap-start rounded-xl border border-navy/6 bg-cream p-4 transition-all duration-500"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(12px)',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <div className="mb-3 overflow-hidden rounded-lg">
                  <img
                    src={shop.image}
                    alt={shop.name}
                    className="h-[140px] w-full object-cover"
                    loading="lazy"
                  />
                </div>

                <h3 className="mb-1 text-[15px] font-semibold text-navy">
                  {shop.name}
                </h3>
                <p className="mb-2 text-[12px] leading-relaxed text-navy/50">
                  {shop.description}
                </p>

                <div className="mb-3 rounded-md bg-navy/[0.03] px-3 py-2.5">
                  <span className="mb-1 block text-[10px] font-medium text-softgold">
                    이 타입에게 맞는 이유
                  </span>
                  <span className="text-[12px] leading-relaxed text-navy/70">
                    {shop.whyMatch}
                  </span>
                </div>

                <div className="mb-3 flex flex-wrap gap-1.5">
                  {shop.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-navy/[0.04] px-2 py-0.5 text-[10px] text-navy/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-start gap-1.5 border-t border-navy/5 pt-2.5">
                  <i className="ri-double-quotes-l mt-0.5 text-[10px] text-softgold/60" />
                  <div>
                    <p className="text-[11px] italic leading-relaxed text-navy/40">
                      {shop.reviewSnippet}
                    </p>
                    <span className="mt-1 block text-[10px] text-navy/30">
                      실제 후기 {shop.reviews}개 기반
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* desktop: grid */}
        <div className="hidden md:grid md:grid-cols-3 md:gap-4">
          {shops.map((shop, i) => (
            <Link key={i} to='/yangbok/detail/test'>
              <div
                key={shop.id}
                className="group rounded-xl border border-navy/6 bg-cream p-4 transition-all duration-500 hover:border-navy/12"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(12px)',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <div className="mb-3 overflow-hidden rounded-lg">
                  <img
                    src={shop.image}
                    alt={shop.name}
                    className="h-[160px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                <h3 className="mb-1 text-[15px] font-semibold text-navy">
                  {shop.name}
                </h3>
                <p className="mb-2 text-[12px] leading-relaxed text-navy/50">
                  {shop.description}
                </p>

                <div className="mb-3 rounded-md bg-navy/[0.03] px-3 py-2.5">
                  <span className="mb-1 block text-[10px] font-medium text-softgold">
                    이 타입에게 맞는 이유
                  </span>
                  <span className="text-[12px] leading-relaxed text-navy/70">
                    {shop.whyMatch}
                  </span>
                </div>

                <div className="mb-3 flex flex-wrap gap-1.5">
                  {shop.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-navy/[0.04] px-2 py-0.5 text-[10px] text-navy/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-start gap-1.5 border-t border-navy/5 pt-2.5">
                  <i className="ri-double-quotes-l mt-0.5 text-[10px] text-softgold/60" />
                  <div>
                    <p className="text-[11px] italic leading-relaxed text-navy/40">
                      {shop.reviewSnippet}
                    </p>
                    <span className="mt-1 block text-[10px] text-navy/30">
                      실제 후기 {shop.reviews}개 기반
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}